import React from "react";
import {
  Bell,
  ChevronDown,
  Clock,
  Lightbulb,
  Video,
  Mail,
  Briefcase,
  Check,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

// ── small building blocks ──────────────────────────────────────────────────
const Eyebrow = ({ children, color = "text-orange-500" }) => (
  <span className={`text-[11px] font-bold tracking-wide uppercase ${color}`}>
    {children}
  </span>
);

const ReadTime = ({ children }) => (
  <span className="flex items-center gap-1 text-xs text-gray-400">
    <Clock size={13} />
    {children}
  </span>
);

const DoneCircle = ({ done = false }) => (
  <span
    className={`flex items-center justify-center w-6 h-6 rounded-full border ${
      done
        ? "bg-emerald-500 border-emerald-500 text-white"
        : "border-gray-300 text-transparent"
    }`}
  >
    <Check size={13} strokeWidth={3} />
  </span>
);

const IconBadge = ({ icon: Icon, className = "" }) => (
  <div
    className={`w-9 h-9 rounded-xl flex items-center justify-center ${className}`}
  >
    <Icon size={17} />
  </div>
);

export default function InterviewTips() {
  return (
    <div className="min-h-screen bg-white px-6 sm:px-10 py-8 font-sans">
      <div className="max-w-6xl mx-auto">
        {/* ── page header ── */}
        <div className="flex items-start justify-between gap-6 mb-8">
          <div>
            <h1 className="text-[34px] leading-tight font-extrabold text-gray-900 tracking-tight">
              Interview Tips
            </h1>
            <p className="mt-2 text-gray-500 text-[15px] leading-relaxed max-w-xl">
              Elevate your interview performance with targeted advice,
              practice questions, and expert strategies designed for the
              modern job market.
            </p>
          </div>
{/* 
          <div className="flex items-center gap-4 flex-shrink-0 pt-1">
            <button
              className="text-gray-500 hover:text-gray-700 transition"
              aria-label="Notifications"
            >
              <Bell size={20} />
            </button>
            <div className="flex items-center gap-1.5 cursor-pointer">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 overflow-hidden ring-1 ring-black/5" />
              <ChevronDown size={16} className="text-gray-400" />
            </div>
          </div> */}
        </div>

        {/* ── hero banner ── */}
        <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-orange-500 via-orange-500 to-amber-400 mb-8">
          <div className="flex flex-col lg:flex-row items-stretch">
            <div className="flex-1 p-8 sm:p-10 flex flex-col justify-center">
              <span className="inline-flex w-fit items-center gap-1.5 bg-white/15 text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-5 backdrop-blur-sm">
                <span className="text-[13px]">◎</span>
                Recommended for You
              </span>

              <h2 className="text-white text-[28px] sm:text-[32px] font-extrabold leading-[1.15] max-w-md mb-4">
                Preparing for a Hotel Receptionist Interview?
              </h2>

              <p className="text-white/85 text-[15px] leading-relaxed max-w-md mb-7">
                We noticed you applied for hospitality roles. Review our
                specialized module covering guest relations, conflict
                resolution scenarios, and front-desk software proficiency.
              </p>

              {/* <button className="w-fit flex items-center gap-2 bg-white text-gray-900 font-semibold text-sm px-5 py-3 rounded-xl hover:bg-gray-50 transition shadow-sm">
                Prepare for Interview
                <ArrowRight size={16} />
              </button> */}
            </div>

            <div className="lg:w-[380px] relative min-h-[220px] lg:min-h-0">
              <div className="absolute inset-4 lg:inset-6 rounded-2xl overflow-hidden bg-gradient-to-br from-slate-700 via-slate-600 to-slate-500">
                {/* stylized front-desk scene */}
                <div className="absolute inset-0 flex items-end justify-center">
                  <div className="w-4/5 h-2/5 bg-slate-200/90 rounded-t-lg" />
                </div>
                <div className="absolute bottom-[15%] left-1/2 -translate-x-1/2 w-20 h-28 bg-slate-800 rounded-t-full" />
                <div className="absolute bottom-[38%] left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-amber-100" />
              </div>
            </div>
          </div>
        </div>

        {/* ── row 1: featured guide + STAR method ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
          {/* featured guide (2/3) */}
          <div className="lg:col-span-2 bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden flex flex-col sm:flex-row">
            <div className="sm:w-2/5 relative min-h-[180px]">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-600 via-slate-500 to-slate-400">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-full bg-slate-300/40" />
                </div>
              </div>
              <span className="absolute bottom-3 left-3 bg-black/60 text-white text-[11px] font-semibold px-2.5 py-1 rounded-md">
                Featured Guide
              </span>
            </div>

            <div className="flex-1 p-6 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <Eyebrow>Common Questions</Eyebrow>
                <ReadTime>8 min read</ReadTime>
              </div>

              <h3 className="text-lg font-bold text-gray-900 leading-snug mb-2">
                Mastering the "Tell Me About Yourself" Pitch
              </h3>

              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                Learn how to structure your professional narrative using the
                Present-Past-Future formula to immediately engage hiring
                managers and set the tone for a successful interview.
              </p>

              {/* <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between">
                <button className="flex items-center gap-1.5 text-orange-500 font-semibold text-sm hover:text-orange-600 transition">
                  Read Full Guide
                  <ArrowRight size={15} />
                </button>
                <button className="flex items-center gap-2 text-gray-500 text-sm font-medium">
                  Mark Done
                  <DoneCircle />
                </button>
              </div> */}
            </div>
          </div>

          {/* STAR method (1/3) */}
          <div className="bg-[#F7F6FB] border border-gray-100 rounded-2xl p-6 flex flex-col">
            <IconBadge
              icon={Lightbulb}
              className="bg-violet-100 text-violet-600 mb-4"
            />
            <Eyebrow color="text-violet-500">Behavioral</Eyebrow>
            <h3 className="text-lg font-bold text-gray-900 leading-snug mt-2 mb-2">
              The STAR Method Explained
            </h3>
            <p className="text-gray-500 text-sm leading-relaxed">
              A step-by-step framework for answering competency-based
              questions effectively without rambling.
            </p>

            <div className="mt-auto pt-5 flex items-center justify-between">
              {/* <ReadTime>4 min read</ReadTime> */}
              <DoneCircle />
            </div>
          </div>
        </div>

        {/* ── row 2: three cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {/* virtual interviews */}
          <div className="bg-[#F7F6FB] border border-gray-100 rounded-2xl p-6 flex flex-col">
            <IconBadge
              icon={Video}
              className="bg-violet-100 text-violet-600 mb-4"
            />
            <Eyebrow>Virtual Interviews</Eyebrow>
            <h3 className="text-base font-bold text-gray-900 leading-snug mt-2 mb-2">
              Setting Up Your Zoom Background
            </h3>
            <p className="text-gray-500 text-sm leading-relaxed">
              Lighting, framing, and minimizing distractions for remote
              interview success.
            </p>

            <div className="mt-auto pt-5 flex items-center justify-between">
              <ReadTime>3 min read</ReadTime>
              <DoneCircle done />
            </div>
          </div>

          {/* thank you email */}
          <div className="bg-[#F7F6FB] border border-gray-100 rounded-2xl p-6 flex flex-col">
            <IconBadge
              icon={Mail}
              className="bg-violet-100 text-violet-600 mb-4"
            />
            <Eyebrow>Follow-Up Tips</Eyebrow>
            <h3 className="text-base font-bold text-gray-900 leading-snug mt-2 mb-2">
              The Perfect Thank You Email
            </h3>
            <p className="text-gray-500 text-sm leading-relaxed">
              Templates and timing for post-interview communication that
              leaves a lasting impression.
            </p>

            <div className="mt-auto pt-5 flex items-center justify-between">
              <ReadTime>5 min read</ReadTime>
              <DoneCircle />
            </div>
          </div>

          {/* CTA */}
          <div className="bg-[#EEF2FF] border border-gray-100 rounded-2xl p-6 flex flex-col items-center text-center justify-center">
            <div className="w-14 h-14 rounded-full bg-orange-500 text-white flex items-center justify-center mb-4">
              <Briefcase size={22} />
            </div>
            <h3 className="text-lg font-bold text-gray-900 leading-snug mb-2">
              Ready for the real thing?
            </h3>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              Browse hundreds of new opportunities tailored to your profile.
            </p>
            <Link to="/jobs">
                <button className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm px-5 py-2.5 rounded-xl transition">
              Find Jobs
              <ArrowRight size={15} />
            </button>

            </Link>
        
          </div>
        </div>
      </div>
    </div>
  );
}